import React, { useEffect, useMemo, useState } from 'react';
import logo from '../../assets/images/yoris_logo.png';
import { useDispatch } from 'react-redux';
import { toggleSideBar } from '../../features/sideBarSlice';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Box, Button, IconButton, InputBase, Stack } from '@mui/material';
import { ArrowDropDown, Language, Menu, Search } from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import MyLink from '../MyLink';

const StyledSearch = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1),
        width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: '12ch',
            '&:focus': {
                width: '20ch',
            },
        },
    },
}));

const NavBar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentPath = useLocation().pathname;

    const [menuBtnItems, setMenuBtnItems] = useState([
        {
            id: 1,
            name: 'Vendors',
            link: '/vendors'
        },
        {
            id: 2,
            name: 'Riders',
            link: '/riders'
        },
    ])
    const [selectedMenuBtnItem, setSelectedMenuBtnItem] = useState({ id: 0, name: 'Customers', link: '/' });
    const toggleMenuBtnItem = (id, index) => {
        const currentItem = selectedMenuBtnItem;
        const selectedItem = menuBtnItems.filter(item => item.id === id)[0];
        setSelectedMenuBtnItem(selectedItem);
        const newMenuBtnItems = [...menuBtnItems];
        newMenuBtnItems.splice(index, 1);
        if (currentItem.name.toLowerCase() === 'customers') {
            newMenuBtnItems.unshift(currentItem);
            setMenuBtnItems(newMenuBtnItems);
        } else {
            newMenuBtnItems.push(currentItem);
            setMenuBtnItems(newMenuBtnItems);
        }
        navigate(selectedItem.link);
    }
    const [showMenuButton, setShowMenuButton] = useState(false);
    useEffect(() => {
        if (currentPath === '/vendors' || currentPath === '/riders') {
            const selectedItem = menuBtnItems.filter(item => item.link === currentPath)[0];
            const selectedItemID = selectedItem.id;
            const selectedItemIndex = menuBtnItems.indexOf(selectedItem);
            toggleMenuBtnItem(selectedItemID, selectedItemIndex);
        }
    }, []);

    const [scrolled, setScrolled] = React.useState(false);

    useEffect(() => {
        const handleScroll = () => {
        if (window.pageYOffset > 0) {
            setScrolled(true);
        } else {
            setScrolled(false);
        }
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
        window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <nav
            className={`fixed top-0 z-[99999] w-full flex flex-col items-center justify-center pt-5 px-3 transition-all ${
                scrolled ? 'shadow-lg' : ''}
            `}>
            <Box
                className='flex items-center px-2 md:px-8 text-sm w-full max-w-7xl bg-[#C3AD60] bg-opacity-20 rounded-xl shadow-md'
                direction='row' alignItems='center' justifyContent='space-between'
            >
                <Box>
                    <Box className='hidden lg:flex'>
                        <Stack
                            className='hidden lg:flex shadow font-sarala py-3 px-6 rounded-3xl' direction='row' gap={2}
                            sx={{ color: 'primary.body', backgroundColor: 'primary.yorisWhite' }}
                        >
                            <Link to='/company'>Company</Link>
                            <MyLink to="blogs">Blogs</MyLink>
                            <Link to='/contact'>Contacts</Link>
                            <MyLink to='faqs'>FAQs</MyLink>
                            <Link to='/donate'>Donate</Link>
                        </Stack>
                    </Box>
                    <Box className='lg:hidden'>
                        <IconButton
                            sx={{ color: 'primary.light' }}
                            className='md:hidden' onClick={() => dispatch(toggleSideBar(true))}
                        >
                            <Menu />
                        </IconButton>
                    </Box>
                </Box>

                <Link to='/#'>
                    <img src={logo} className='h-14 md:h-16 lg:h-20' alt='' />
                </Link>

                <Stack direction='row' alignItems='center' gap={1}>
                    <Box className='hidden lg:flex'>
                        <StyledSearch>
                            <SearchIconWrapper>
                                <Search />
                            </SearchIconWrapper>
                            <StyledInputBase
                                placeholder="Search…"
                                inputProps={{ 'aria-label': 'search' }}
                            />
                        </StyledSearch>
                    </Box>
                    <Box className='hidden lg:flex items-center'>
                        <IconButton size='small' sx={{ padding: '0px !important' }}
                        >
                            <Language />
                        </IconButton>
                    </Box>
                    <Button
                        sx={{
                            backgroundColor: 'primary.light',
                            color: 'primary.yorisWhite',
                            borderRadius: '20px', padding: '4px 8px',
                            textTransform: 'none', minWidth: '80px'
                        }}
                    >
                        Sign Up
                    </Button>
                </Stack>
            </Box>
            <Box className='w-full max-w-7xl hidden lg:flex justify-end pt-2'>
                <Box className='flex flex-col items-end gap-3'
                    onMouseLeave={() => setShowMenuButton(false)}
                    onMouseEnter={() => setShowMenuButton(true)}
                >
                    <Button variant='outlined' size='large' className='shadow-md'
                        sx={{
                            color: 'primary.dark', backgroundColor: 'primary.yorisWhite',
                            borderRadius: '20px', textTransform: 'none', fontWeight: '600',
                            '&:hover': {
                                color: 'primary.yorisWhite',
                                backgroundColor: '#15130A'
                            }
                        }}
                    >
                        {selectedMenuBtnItem.name} <ArrowDropDown />
                    </Button>
                    {
                        showMenuButton &&
                        menuBtnItems.map((item, index) => (
                            <Button variant='outlined' size='large' className='shadow-md' key={index + 1} onClick={() => toggleMenuBtnItem(item.id, index)}
                                sx={{
                                    color: 'primary.dark', backgroundColor: 'primary.yorisWhite',
                                    borderRadius: '20px', textTransform: 'none', fontWeight: '600',
                                    '&:hover': {
                                        color: 'primary.yorisWhite',
                                        backgroundColor: '#15130A'
                                    }
                                }}
                            >
                                {item.name}
                            </Button>
                        ))
                    }
                </Box>
            </Box>
        </nav>
    )
}

export default NavBar;